import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import HeroSection from "@/components/home/HeroSection";
import { ServicesPreview, AboutPreview } from "@/components/home/ContentSections";
import { StatsSection, EstimatorSection, CTASection } from "@/components/home/DataSections";
import { toast } from "sonner";
import { 
  Home as HomeIcon,
  Hammer,
  Shield
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  { id: "service-loft", icon: HomeIcon, title: "Loft Conversions", description: "Transform unused attic space into stunning living areas, bedrooms, or home offices." },
  { id: "service-extension", icon: Hammer, title: "Extensions", description: "Expand your home with expertly crafted side, rear, or garage conversions." },
  { id: "service-kitchen", icon: Shield, title: "Kitchen & Bathroom", description: "Complete fitting services from custom cabinetry to full bathroom renovations." }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    
    const fetchProjectTypes = async () => {
      try {
        const response = await axios.get(`${API}/project-types`, {
          signal: controller.signal
        });
        setProjectTypes(response.data);
      } catch (err) {
        if (!axios.isCancel(err)) {
          toast.error('Failed to load project types. Please refresh the page.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjectTypes();
    return () => controller.abort();
  }, []);

  const handleProjectSelect = useCallback((projectId) => {
    navigate(`/estimator/${projectId}`);
  }, [navigate]);

  const formatPrice = useCallback((min, max) => {
    const formatNum = (num) => num >= 1000 ? `£${(num / 1000).toFixed(0)}k` : `£${num}`;
    return `${formatNum(min)} - ${formatNum(max)}`;
  }, []);

  const scrollToEstimator = useCallback(() => {
    document.getElementById('estimator')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Trusted Builder in Surrey & SW London"
        description="Quality carpentry & construction across Surrey & SW London. Extensions, loft conversions, kitchens & renovations. Fully insured. Free quotes. 07780 996775."
        keywords="carpentry surrey, builder sw london, home improvements, loft conversion, house extension"
        url="/"
      />
      <Header />
      <HeroSection onGetEstimate={scrollToEstimator} />
      <StatsSection />
      <ServicesPreview services={services} />
      <AboutPreview />
      <EstimatorSection
        projectTypes={projectTypes}
        loading={loading}
        formatPrice={formatPrice}
        onProjectSelect={handleProjectSelect}
      />
      <CTASection />
      <Footer />
    </div>
  );
};

export default HomePage;
